/**
 * Command Teaching Corpus（OPT-202，红线 #9：唯一 typed 定义 + 唯一构建器）
 * ===========================================================================
 *
 * 业务背景：非技术员工（营销/运营/投放/BD/行政）跑常见只读命令（ls / grep / git status...）
 * 也会被 DOSIA 弹窗询问，体验像"总在被工程师语言拷问"。本模块做两件事：
 *   1. `COMMAND_WHITELIST` —— 一份「已验证只读命令」清单，会话构建时 union 进 SDK
 *      `settings.permissions.allow`（session-manager.ts `applyCommandWhitelist`），命中
 *      即静默放行，不再弹窗。
 *   2. `BLOCKED_COMMAND_HINTS` —— 命令被拦/被问时，配一段人话解释"这是干什么的、为什么
 *      要问你、换什么做法"，供 PermissionDialog 在弹窗里展示（而不是干巴巴的工具名+参数）。
 *
 * 数据源：craft-agents-oss `apps/electron/resources/permissions/default.json`
 * （2026-03-07 版，221 条 allowedBashPatterns + 7 条 blockedCommandHints，只读参考，
 * 未拷贝该仓库代码 —— 本文件是 DOSIA 侧独立 typed 转写 + 全量中文化）。
 *
 * ── 形态转换（craft 裸正则 -> DOSIA "Bash(...)" 规则串）───────────────────
 * DOSIA 的 Bash 权限规则语法有两种理论上可行的形态（三仓对撞 claude-code
 * shellRuleMatching.ts 逐字确认）——`Bash(word:*)`（冒号+星号 legacy prefix）与
 * `Bash(word *)`（空格+星号 wildcard）——但**本仓库实际打包的 CLI 引擎版本只对后者
 * 生效**（2026-07-11 CDP e2e 实测：冒号+星号形态下 `ls` 仍然弹窗，换成空格+星号后
 * 才真正静默放行；空格+星号也是 session-manager.ts `allCliTools.map(tool =>
 * \`Bash(\${tool} *)\`)` 已在生产验证过的形态，docs/sdk-learnings.md §21.5 同样记载）。
 * 本文件全部规则串统一用 `Bash(word *)` / `Bash(tool sub *)`。另有 `Bash(word)`
 * exact 语法（只匹配裸 "word"，不含任何参数，用于 bare-invocation 场景）。
 *   注意：结尾恰好是字面 ":*" 的字符串会被 CLI 引擎当 legacy prefix 解析 —— 因此任何
 *   "必须保留字面冒号" 的语义（如 PowerShell $env:VAR）在 DOSIA 语法下都无法安全表达
 *   （不只是本仓库这个版本的问题，是两种语法本身的语义冲突）。
 *
 * ── 产品性剔除（不计入"转换失败"，是明确的产品适配判断）────────────────
 *   - 61 条 Windows PowerShell 专用规则（Get-* / Test-* / ConvertTo-* / $env: 等）：
 *     DOSIA 是 macOS-only Electron app（CLAUDE.md 产品定位），PowerShell 命令在员工机器
 *     上永不出现，导入=死配置，不纳入。
 *   - 19 条 craft 专有 CLI 工具（pdf-tool / xlsx-tool / docx-tool / pptx-tool / img-tool /
 *     ical-tool / doc-diff / craft-agent 全部子命令 / browser-tool）：这些是
 *     craft-agents-oss 自带的二进制，DOSIA 未内置同名工具，规则永不命中，不纳入。
 *     （markitdown 是公开通用工具，非 craft 专有，予以保留。）
 *
 * ── 「伪只读」安全剔除（P0 洞根治，2026-07-11）★核心不变量★ ─────────────────
 * **地板生效序**（permission-resolver.ts 头注释）：deny → ask → 工具级检查 → bypass →
 * **allow 规则（静默放行）** → passthrough 才到 resolver。而三条不可配安全地板
 * （isDangerousCommand 74 黑名单 / workspace 越界 / 子会话 fail-closed）**全在 resolver
 * ——最后一步、只对 passthrough 命令生效**。⇒ 任何命中本白名单 allow 规则的命令在「静默
 * 放行」层就被放过，**永远到不了三条地板**。所以本白名单里任何「能通过 flag/子命令/配置
 * exec / write / delete」的伪只读命令 = 绕过全部安全兜底的洞，必须整条剔除（DOSIA prefix/
 * wildcard 语法无否定，无法只排除危险 flag，只能整条删）。
 *
 * 关键判据 =「CLI 引擎看得见吗」：引擎会切分复合命令（`;` `|` `&&`）并识别重定向（`cmd > f`
 * 不被 `Bash(cmd *)` 覆盖），所以 shell 层的写/执行仍被拦。**危险的是命令自身参数语法里的
 * 内部 exec/write**（find `-exec`、sed `w`/`e`、rg `--pre`、yq `-i`、sort `-o` …）——这些不是
 * shell 算符、引擎切不到看不见，命中 allow 即静默放行。据此剔除以下各类（详见各数据段就近注释）：
 *   - **内部 exec（任意命令执行）**：find(-exec/-delete)、fd(-x)、rg(--pre)、sed(w/e，连 -n 也剔)、
 *     npm/pnpm audit(→ audit fix → install postinstall)。—— HIGH。
 *   - **前导通配 `Bash(* --help*)` / `Bash(* -h)`**：`*` 开头 = 给任意命令根开口子，地板整体失效。
 *     —— HIGH，两条直接删（整个「帮助文档」组随之清空）。
 *   - **任意文件写（内容可控）**：markitdown(-o)、yq(-i)、xmllint(--output)、sort(-o)、uniq(OUTPUT 操作数)。—— MEDIUM。
 *   - **分页器 shell 逃逸（TTY `!cmd` / LESSOPEN / MANPAGER）**：less/more/bat/man/fzf。DOSIA agent
 *     无 TTY 时无法触发，保守剔除（防御纵深 + 未来挂 PTY 即活）。—— LOW（TTY 门控）。
 *   - **ref 删除/改写（且绕过地板显式项）**：git branch(-D，bash-patterns 显式列的危险项！)/tag(-d)/
 *     remote(set-url)/reflog(delete/expire)。—— LOW-MED（本地引用，reflog 可恢复）。
 *   awk/gawk/mawk/nawk（system() 借壳执行）本就未导入、只留 BLOCKED_COMMAND_HINTS 教学；find/sed
 *   同款加入教学提示。**新增防御测试**（本文件 .test.ts）：EXEC_WRITE_DENYLIST 命令根白名单化即
 *   当场红（除显式例外表，如 `python -m json.tool`）+ 断言无任何前导通配规则 —— 挡同类回归。
 *
 * ── 其它保守丢弃（语法无法 1:1 表达）────────────────────────────────────────
 *   - awk / gawk / mawk / nawk（4 条）：craft 源注释明确"仅在命令未出现 system()/
 *     命令管道 getline 等执行原语时才安全"，依赖 craft CLI 自身的第二层 validator 做内容级
 *     拦截 —— DOSIA 的 permission-resolver 没有等价机制。若照单放行 Bash(awk *)，
 *     `awk 'BEGIN{system("...")}'` 会被静默放行，故不导入白名单，只保留教学提示。
 *   - gh api（全部剔除，0 条进白名单）：craft 用否定先行断言排除 --method 非 GET 的调用；DOSIA
 *     语法无否定，`Bash(gh api *)` 会连带静默放行 `gh api --method POST/DELETE`（远程写/删）——
 *     与本节「伪只读」同类洞，故**不保留任何 gh api 规则**（GET 查询正常同意一次询问即可）。
 *   - $env:（1 条）：见上方冒号语义冲突说明，无法安全表达，丢弃。
 *
 * ── 展开策略（安全方向：只收窄，不放宽）───────────────────────────────
 * craft 用 "tool (a|b|c)" 这类整族正则表达多个只读子命令；DOSIA 语法没有 OR，
 * 逐个子命令展开成独立 Bash(tool sub *) 规则 —— 例如 git (status|log|diff|...)
 * 展开成约 20 条 Bash(git status *) / Bash(git log *) / ... 。刻意不做
 * Bash(git *) 整族放行：那会连带放行 git push --force / git reset --hard
 * 等破坏性操作。docker / kubectl / npm / gh 等同理全部逐子命令展开。
 *
 * 最终有效规则数 = COMMAND_WHITELIST.length（vitest 断言具体值，不在注释里手写易漂移的
 * 数字）。经此转换的条目全部满足 isValidPermissionRule（enterprise-admin.ts 同一份
 * 校验器，vitest 交叉验证）。
 */
/**
 * 裸词前缀规则 —— 对应 craft `^word\b`。命中 "word" 本身或 "word <任意参数>"。
 * 不带 category（各分组数组统一用 withCategory() 一次性套上，避免 70+ 次调用逐条传参
 * 漏传——2026-07-11 实证：早期版本 category 位置传参在批量转写时大面积漏传，
 * 静默产出 category: undefined，vitest 数据完整性测试当场抓到，遂改成本设计）。
 *
 * 规则串用 `Bash(word *)`（空格+星号 wildcard 形态），**不用** `Bash(word:*)`
 * （冒号+星号 legacy prefix 形态）—— 2026-07-11 CDP e2e 实证：本仓库打包的 CLI 引擎
 * 版本对 permissions.allow 里的冒号前缀语法未按预期生效（ls 仍弹窗），而空格+星号是
 * session-manager.ts 里 `allCliTools.map(tool => \`Bash(\${tool} *)\`)`（4461 行）已在
 * 生产验证过的形态（docs/sdk-learnings.md §21.5 同样记载 permissions.allow 用
 * "Bash(tool *)"）。冒号+星号只在 sandbox.excludedCommands 通道被证实好使，两条通道
 * 语法不通用，禁混用（同 sdk-learnings 教训）。
 */
function bareWord(word, commentZh, source) {
    return { id: word, rule: `Bash(${word} *)`, commentZh, source: source ?? `^${word}\\b` };
}
/** 给一整个分组数组统一套上 category（见 bareWord doc 注释——防漏传的唯一收口点）。 */
function withCategory(category, entries) {
    return entries.map((e) => ({ ...e, category }));
}
/** 精确匹配规则（不带参数）—— 对应 craft `^word\s*$` 或 `\b$` 锚定的"仅裸调用"形态。 */
function exactCmd(id, cmd, commentZh, category, source) {
    return { id, rule: `Bash(${cmd})`, commentZh, category, source };
}
/** wildcard 规则 —— 用于"标志在命令中间/结尾任意位置出现"的场景（如 --help / --noEmit）。 */
function wildcardCmd(id, pattern, commentZh, category, source) {
    return { id, rule: `Bash(${pattern})`, commentZh, category, source };
}
/**
 * 子命令族展开 —— 对应 craft `tool (sub1|sub2|...)` 整族正则。逐子命令展开成独立 prefix
 * 规则，刻意不整族放行（安全方向：只收窄不放宽，见文件头「展开策略」）。
 */
function family(tool, source, category, verbs, idPrefix) {
    // idPrefix override: 两个不同 tool 字符串（"docker-compose" / "docker compose"）
    // space→dash 折叠后会撞出同一 id 前缀——显式传 idPrefix 避免这种巧合碰撞。
    const prefix = idPrefix ?? tool.replace(/\s+/g, "-");
    return verbs.map(([verbId, verbCmd, commentZh]) => ({
        id: `${prefix}-${verbId}`,
        rule: `Bash(${tool} ${verbCmd} *)`,
        commentZh,
        category,
        source,
    }));
}
/** 版本号查询 —— 工具名 + 固定版本 flag（--version / -v 等），两个 flag 变体共用同一句说明。 */
function versionCheck(tool, flags, commentZh, source) {
    return flags.map(([flagId, flag]) => ({
        id: `${tool}-${flagId}`,
        rule: `Bash(${tool} ${flag} *)`,
        commentZh,
        category: "版本号查询",
        source,
    }));
}
// ---- 数据：文件与目录浏览 ---------------------------------------------------
const FILE_BROWSING = withCategory("文件与目录", [
    bareWord("ls", "列出当前目录下的文件和文件夹（只读，不会新建/删除任何东西）"),
    bareWord("ll", "以详细列表方式查看目录内容（ls -l 的简写别名）"),
    bareWord("la", "查看目录内容，包含隐藏文件（ls -la 的简写别名）"),
    // tree 已剔除：`-o <file>`/`-H`(HTML) 把输出写到任意路径 —— 越界写地板被绕过。看目录用 ls。
    bareWord("file", "识别一个文件是什么类型（图片/文本/压缩包等），不打开不修改"),
    bareWord("stat", "查看文件的详细信息（大小、修改时间等），不修改文件"),
    bareWord("du", "统计文件/文件夹占用的磁盘空间大小"),
    bareWord("df", "查看磁盘剩余空间"),
    // find 已剔除：-exec/-execdir 执行任意命令、-delete 删文件、-fprintf 写任意路径 —— 全是
    // 命令自身的参数语法（非 shell 算符/重定向），CLI 引擎切分不到、看不见，命中 allow 规则即
    // 静默放行、永不落到 permission-resolver 三条地板。伪只读，见文件头「伪只读剔除」。
    bareWord("locate", "用系统索引快速查找文件路径（只读）"),
    bareWord("which", "查一个命令实际调用的是哪个程序（定位可执行文件路径）"),
    bareWord("whereis", "查找命令、源码、帮助文档的位置"),
    bareWord("type", "查看某个命令是内置命令、别名还是外部程序"),
    // markitdown 已剔除：-o <file> 把（内容可控的）转换结果写到任意路径 —— 越界写地板被绕过。
]);
// ---- 数据：文本处理 ---------------------------------------------------------
const TEXT_PROCESSING = withCategory("文本处理", [
    bareWord("head", "查看文件开头几行内容"),
    bareWord("tail", "查看文件末尾几行内容（常用来看日志最新内容）"),
    bareWord("cat", "把文件内容打印出来看（只读查看，不修改文件）"),
    // less / more / bat 已剔除：分页器在 TTY 下支持 `!cmd` shell 逃逸、并会派生 $PAGER/
    // LESSOPEN 输入预处理器执行命令 —— 均是分页器自身机制、CLI 引擎看不见。地板对分页逃逸零
    // 覆盖（bash-patterns 74 条无分页器项），一旦逃逸即全绕。DOSIA agent 无 TTY 时无法触发，
    // 但保守剔除（防御纵深 + 未来若挂 PTY 即活）。查看文件请用 cat/head/tail。
    bareWord("wc", "统计文件的行数/字数/字节数"),
    bareWord("nl", "给文本内容加上行号方便阅读"),
    bareWord("grep", "在文件里搜索关键词，只读查找不修改文件"),
    // rg / fd 已剔除：ripgrep `--pre <cmd>` 对每个文件执行任意命令、fd `-x/--exec/-X` 对每个
    // 结果执行任意命令 —— 与 find -exec 同一「内部 exec」类，命中 allow 即静默放行、绕过地板。
    // DOSIA 语法无否定，无法排除 --pre/-x，只能整条剔除。搜索请用 grep/ag/ack。
    bareWord("ag", "关键词搜索工具（The Silver Searcher），只读"),
    bareWord("ack", "面向代码的关键词搜索工具，只读"),
    // fzf 已剔除：`--preview`/`--bind execute(...)` 在交互选中时执行任意命令（TTY 门控，同分页器）。
    // sort / uniq 已剔除：sort `-o <file>` 与 uniq 的第二个 OUTPUT 操作数把（内容可控的）结果写
    // 到任意路径 —— 越界写地板被绕过（喂预排序/无重复输入即可逐字写任意内容）。
    bareWord("cut", "从每行文本里截取指定的列/字段"),
    bareWord("tr", "替换/删除文本里的字符（作用于打印输出，不改原文件）"),
    bareWord("column", "把文本内容对齐排版成表格样式方便阅读"),
    // sed 已整条剔除（连 sed -n 也剔）：sed 即使带 -n，`w file`/`W file`/`s///w file` 仍写任意
    // 文件、`e` 命令执行 shell —— -n 只关"自动打印"，不关写/执行。见文件头 + BLOCKED_COMMAND_HINTS。
]);
// ---- 数据：数据格式处理 -----------------------------------------------------
const DATA_FORMAT = withCategory("数据格式", [
    bareWord("jq", "查看/筛选 JSON 数据内容，只读打印不改文件"),
    // yq 已剔除：`-i/--inplace` 原地改写文件、并可写任意路径 —— 越界写地板被绕过（jq 无写文件
    // 能力故保留；yq 是另一套 Go 工具，有 -i 写）。
    bareWord("xq", "查看/筛选 XML 数据内容，只读打印不改文件"),
    // xmllint 已剔除：`--output <file>` 把（内容可控的）格式化结果写到任意路径 —— 越界写绕过。
    bareWord("json_pp", "把 JSON 内容格式化成更易读的排版"),
    exactCmd("python-json-tool", "python -m json.tool", "用 Python 自带工具把 JSON 格式化成更易读的排版", "数据格式", "^python\\s+-m\\s+json\\.tool\\b"),
]);
// ---- 数据：系统信息 ---------------------------------------------------------
const SYSTEM_INFO = withCategory("系统信息", [
    bareWord("cd", "切换当前工作目录（只影响当前会话，不改动任何文件）"),
    bareWord("pwd", "查看当前所在的目录路径"),
    bareWord("whoami", "查看当前登录的系统用户名"),
    bareWord("id", "查看当前用户的身份信息（用户组等）"),
    bareWord("groups", "查看当前用户所属的用户组"),
    bareWord("uname", "查看操作系统与硬件架构信息"),
    bareWord("hostname", "查看电脑的网络主机名"),
    bareWord("date", "查看当前系统日期和时间"),
    bareWord("uptime", "查看电脑已开机运行了多久、系统负载"),
    bareWord("printenv", "查看某个或全部环境变量的值"),
    exactCmd("env-bare", "env", "查看当前会话的全部环境变量（不带参数的只读用法）", "系统信息", "^env$"),
    bareWord("echo", "把文字打印到屏幕（只读；命令替换/重定向写文件等用法仍会被拦）"),
    bareWord("ps", "查看当前正在运行的程序/进程列表"),
    bareWord("htop", "交互式查看系统进程和资源占用（只读监控）"),
    bareWord("free", "查看内存使用情况"),
    bareWord("vmstat", "查看内存/CPU 等系统运行状态统计"),
    bareWord("iostat", "查看磁盘读写的性能统计"),
    bareWord("lscpu", "查看 CPU 型号、核心数等信息"),
    bareWord("lsmem", "查看内存条/内存区块信息"),
    bareWord("lsblk", "查看磁盘和分区列表"),
    bareWord("lsusb", "查看已连接的 USB 设备列表"),
    bareWord("lspci", "查看已连接的 PCI 硬件设备列表"),
    {
        id: "top-list-mode",
        rule: "Bash(top -l *)",
        commentZh: "以列表模式查看系统进程占用情况（只读快照，不进入交互界面）",
        category: "系统信息",
        source: "^top\\s+-[lb]",
    },
    {
        id: "top-batch-mode",
        rule: "Bash(top -b *)",
        commentZh: "以批处理模式查看系统进程占用情况（只读输出，不进入交互界面）",
        category: "系统信息",
        source: "^top\\s+-[lb]",
    },
]);
// ---- 数据：网络诊断 ---------------------------------------------------------
const NETWORK_DIAG = withCategory("网络诊断", [
    bareWord("ping", "测试网络是否连通到某个地址（只读探测）"),
    bareWord("traceroute", "追踪网络请求经过了哪些节点，排查网络慢/不通的问题"),
    bareWord("tracepath", "类似 traceroute 的网络路径追踪工具"),
    bareWord("mtr", "持续追踪网络路径质量的诊断工具"),
    bareWord("dig", "查询域名对应的服务器地址（DNS 查询，只读）"),
    bareWord("nslookup", "查询域名解析信息（DNS 查询，只读）"),
    bareWord("host", "查询域名对应的 IP 地址"),
    bareWord("netstat", "查看当前网络连接和端口占用情况"),
    bareWord("ss", "查看网络连接状态（netstat 的更快替代）"),
    bareWord("ifconfig", "查看网卡和网络接口信息"),
    ...family("ip", "^ip\\s+(addr|link|route|neigh)\\s*(show)?\\b", "网络诊断", [
        ["addr", "addr", "查看网络接口的 IP 地址信息"],
        ["link", "link", "查看网络接口（网卡）状态信息"],
        ["route", "route", "查看网络路由表"],
        ["neigh", "neigh", "查看局域网内其它设备的地址表（ARP 缓存）"],
    ]),
]);
// ---- 数据：版本控制（git / gh） ---------------------------------------------
const GIT_SOURCE = "^git\\s+((-[A-Za-z]|--[a-z][-a-z]*)(\\s+[^\\s-][^\\s]*)?\\s+)*(status|log|diff|show|branch|tag|remote|stash\\s+list|describe|rev-parse|config\\s+--get|config\\s+-l|ls-files|ls-tree|shortlog|blame|annotate|reflog|cherry|whatchanged|ls-remote|history)\\b";
const GIT_READ_ONLY_ENTRIES = family("git", GIT_SOURCE, "版本控制", [
    ["status", "status", "查看当前有哪些文件被改动、还没提交"],
    ["log", "log", "查看提交历史记录"],
    ["diff", "diff", "查看代码具体改了哪些内容"],
    ["show", "show", "查看某次提交的详细改动内容"],
    // git branch / tag / remote / reflog 已剔除：它们的裸命令是"管理器"动词，通过 `Bash(git
    // branch *)` 等宽 scope 可达破坏性子命令 —— `git branch -D`（删分支，且是 bash-patterns
    // 地板显式列的危险项，白名单化即绕过该地板）、`git tag -d/-f`（删/强改标签）、`git remote
    // remove/set-url`（改远程）、`git reflog delete/expire`（删恢复引用）。DOSIA prefix 语法无
    // 法排除这些子命令，整族剔除；只读列举可正常同意一次询问。status/log/diff/show 等纯读保留。
    ["stash-list", "stash list", "查看临时保存（stash）的改动列表"],
    ["describe", "describe", "查看当前代码对应的版本描述"],
    ["rev-parse", "rev-parse", "查询提交哈希等版本控制内部信息"],
    ["config-get", "config --get", "查看某一项 git 配置的值"],
    ["config-list", "config -l", "查看全部 git 配置项"],
    ["ls-files", "ls-files", "查看仓库里已被 git 追踪的文件列表"],
    ["ls-tree", "ls-tree", "查看某个版本下的目录树结构"],
    ["shortlog", "shortlog", "按作者汇总提交记录"],
    ["blame", "blame", "查看某个文件每一行是谁改的、哪次提交改的"],
    ["annotate", "annotate", "同 blame，标注每行代码的提交来源"],
    ["cherry", "cherry", "对比两个分支间还未合并的提交"],
    ["whatchanged", "whatchanged", "查看历史提交都改了哪些文件"],
    ["ls-remote", "ls-remote", "查看远程仓库的分支/标签列表"],
    ["history", "history", "查看提交历史（别名用法）"],
]);
const GH_ENTITIES = [
    ["pr", "Pull Request"],
    ["issue", "Issue"],
    ["repo", "仓库"],
    ["release", "发布版本"],
    ["run", "工作流运行记录"],
    ["workflow", "工作流"],
    ["gist", "代码片段"],
    ["project", "项目看板"],
];
const GH_VERBS = [
    ["view", "查看详情"],
    ["list", "列表"],
    ["status", "状态"],
    ["diff", "改动对比"],
    ["checks", "检查结果"],
    ["comments", "评论"],
];
const GH_SOURCE = "^gh\\s+(pr|issue|repo|release|run|workflow|gist|project)\\s+(view|list|status|diff|checks|comments)\\b";
const GH_ENTRIES = GH_ENTITIES.flatMap(([entity, label]) => family(`gh ${entity}`, GH_SOURCE, "版本控制", GH_VERBS.map(([verb, verbZh]) => [verb, verb, `查看 GitHub ${label}的${verbZh}（只读）`])));
const VERSION_CONTROL = [
    ...GIT_READ_ONLY_ENTRIES,
    ...GH_ENTRIES,
    // gh auth status / gh config get|list —— craft 里各自独立成条，非 entity x verb 族。
    {
        id: "gh-auth-status",
        rule: "Bash(gh auth status *)",
        commentZh: "查看 GitHub 命令行工具的登录状态",
        category: "版本控制",
        source: "^gh\\s+auth\\s+status\\b",
    },
    ...family("gh config", "^gh\\s+config\\s+(get|list)\\b", "版本控制", [
        ["get", "get", "查看 GitHub 命令行工具的某一项配置"],
        ["list", "list", "查看 GitHub 命令行工具的全部配置"],
    ]),
];
// ---- 数据：包管理只读查询 ---------------------------------------------------
const PACKAGE_MGMT = [
    ...family("npm", "^npm\\s+(ls|list|view|info|show|outdated|audit|search|explain|why|config\\s+get|config\\s+list)\\b", "包管理", [
        ["ls", "ls", "查看 npm 已安装的依赖包列表"],
        ["list", "list", "查看 npm 已安装的依赖包列表"],
        ["view", "view", "查看某个 npm 包的详情"],
        ["info", "info", "查看某个 npm 包的详情"],
        ["show", "show", "查看某个 npm 包的详情"],
        ["outdated", "outdated", "查看哪些依赖包有新版本可更新"],
        // npm audit 已剔除：`Bash(npm audit *)` 可达 `npm audit fix` —— 它会跑 npm install、
        // 触发依赖的 postinstall 生命周期脚本 = 任意代码执行，且改写 package.json/lockfile。
        // 只读扫描请正常同意一次询问。（yarn classic 的 audit 无 fix 子命令、纯报告，故保留。）
        ["search", "search", "搜索 npm 上的包"],
        ["explain", "explain", "查看某个依赖包为什么会被安装（依赖链）"],
        ["why", "why", "查看某个依赖包为什么会被安装（依赖链）"],
        ["config-get", "config get", "查看 npm 的某一项配置"],
        ["config-list", "config list", "查看 npm 的全部配置"],
    ]),
    ...family("yarn", "^yarn\\s+(list|info|why|outdated|audit)\\b", "包管理", [
        ["list", "list", "查看 yarn 已安装的依赖包列表"],
        ["info", "info", "查看某个依赖包的详情"],
        ["why", "why", "查看某个依赖包为什么会被安装（依赖链）"],
        ["outdated", "outdated", "查看哪些依赖包有新版本可更新"],
        ["audit", "audit", "扫描依赖包是否有已知安全漏洞"],
    ]),
    ...family("pnpm", "^pnpm\\s+(list|ls|why|outdated|audit)\\b", "包管理", [
        ["list", "list", "查看 pnpm 已安装的依赖包列表"],
        ["ls", "ls", "查看 pnpm 已安装的依赖包列表"],
        ["why", "why", "查看某个依赖包为什么会被安装（依赖链）"],
        ["outdated", "outdated", "查看哪些依赖包有新版本可更新"],
        // pnpm audit 已剔除：`Bash(pnpm audit *)` 可达 `pnpm audit --fix` —— 改写 lockfile/装包，
        // 同 npm audit fix 的执行/写入风险。只读扫描请正常同意一次询问。
    ]),
    {
        id: "bun-pm-ls",
        rule: "Bash(bun pm ls *)",
        commentZh: "查看 bun 已安装的依赖包列表",
        category: "包管理",
        source: "^bun\\s+(pm\\s+ls)\\b",
    },
    ...family("pip", "^pip\\s+(list|show|freeze|check)\\b", "包管理", [
        ["list", "list", "查看已安装的 Python 包列表"],
        ["show", "show", "查看某个 Python 包的详情"],
        ["freeze", "freeze", "导出当前已安装 Python 包及版本清单"],
        ["check", "check", "检查已安装 Python 包之间是否有依赖冲突"],
    ]),
    ...family("pip3", "^pip3\\s+(list|show|freeze|check)\\b", "包管理", [
        ["list", "list", "查看已安装的 Python 包列表"],
        ["show", "show", "查看某个 Python 包的详情"],
        ["freeze", "freeze", "导出当前已安装 Python 包及版本清单"],
        ["check", "check", "检查已安装 Python 包之间是否有依赖冲突"],
    ]),
    ...family("cargo", "^cargo\\s+(tree|metadata|pkgid|verify-project)\\b", "包管理", [
        ["tree", "tree", "查看 Rust 项目的依赖关系树"],
        ["metadata", "metadata", "查看 Rust 项目的依赖元数据"],
        ["pkgid", "pkgid", "查看 Rust 包的唯一标识"],
        ["verify-project", "verify-project", "校验 Rust 项目配置文件是否有效"],
    ]),
    ...family("go", "^go\\s+(list|mod\\s+graph|mod\\s+why|version)\\b", "包管理", [
        ["list", "list", "查看 Go 项目的模块/包列表"],
        ["mod-graph", "mod graph", "查看 Go 项目的依赖关系图"],
        ["mod-why", "mod why", "查看某个 Go 依赖为什么会被引入"],
    ]),
    ...family("composer", "^composer\\s+(show|info|outdated|licenses)\\b", "包管理", [
        ["show", "show", "查看 PHP 依赖包信息"],
        ["info", "info", "查看 PHP 依赖包信息"],
        ["outdated", "outdated", "查看哪些 PHP 依赖包有新版本"],
        ["licenses", "licenses", "查看依赖包的开源许可证信息"],
    ]),
    ...family("gem", "^gem\\s+(list|info|dependency|environment)\\b", "包管理", [
        ["list", "list", "查看已安装的 Ruby 包列表"],
        ["info", "info", "查看某个 Ruby 包的详情"],
        ["dependency", "dependency", "查看 Ruby 包的依赖关系"],
        ["environment", "environment", "查看 Ruby 运行环境信息"],
    ]),
    ...family("bundle", "^bundle\\s+(list|info|outdated)\\b", "包管理", [
        ["list", "list", "查看 Ruby 项目锁定的依赖包列表"],
        ["info", "info", "查看某个依赖包的详情"],
        ["outdated", "outdated", "查看哪些依赖包有新版本"],
    ]),
];
// ---- 数据：版本号查询 -------------------------------------------------------
const VERSION_QUERIES = [
    ...versionCheck("node", [["long", "--version"], ["short", "-v"]], "查看 Node.js 的版本号", "^node\\s+(--version|-v)\\b"),
    ...versionCheck("npm", [["long", "--version"], ["short", "-v"]], "查看 npm 的版本号", "^npm\\s+(--version|-v)\\b"),
    ...versionCheck("yarn", [["long", "--version"], ["short", "-v"]], "查看 yarn 的版本号", "^yarn\\s+(--version|-v)\\b"),
    ...versionCheck("pnpm", [["long", "--version"], ["short", "-v"]], "查看 pnpm 的版本号", "^pnpm\\s+(--version|-v)\\b"),
    ...versionCheck("bun", [["long", "--version"], ["short", "-v"]], "查看 bun 的版本号", "^bun\\s+(--version|-v)\\b"),
    ...versionCheck("python", [["long", "--version"], ["short", "-V"]], "查看 Python 的版本号", "^python\\s+(--version|-V)\\b"),
    ...versionCheck("python3", [["long", "--version"], ["short", "-V"]], "查看 Python 3 的版本号", "^python3\\s+(--version|-V)\\b"),
    ...versionCheck("ruby", [["long", "--version"], ["short", "-v"]], "查看 Ruby 的版本号", "^ruby\\s+(--version|-v)\\b"),
    {
        id: "go-version",
        rule: "Bash(go version *)",
        commentZh: "查看 Go 的版本号",
        category: "版本号查询",
        source: "^go\\s+version\\b",
    },
    ...versionCheck("rustc", [["long", "--version"], ["short", "-V"]], "查看 Rust 编译器的版本号", "^rustc\\s+(--version|-V)\\b"),
    ...versionCheck("cargo", [["long", "--version"], ["short", "-V"]], "查看 Cargo（Rust 包管理器）的版本号", "^cargo\\s+(--version|-V)\\b"),
    ...versionCheck("java", [["long", "--version"], ["short", "-version"]], "查看 Java 的版本号", "^java\\s+(-version|--version)\\b"),
    {
        id: "dotnet-version",
        rule: "Bash(dotnet --version *)",
        commentZh: "查看 .NET 的版本号",
        category: "版本号查询",
        source: "^dotnet\\s+--version\\b",
    },
    ...versionCheck("php", [["long", "--version"], ["short", "-v"]], "查看 PHP 的版本号", "^php\\s+(--version|-v)\\b"),
    ...versionCheck("perl", [["long", "--version"], ["short", "-v"]], "查看 Perl 的版本号", "^perl\\s+(--version|-v)\\b"),
];
// ---- 数据：容器与集群只读查看 -----------------------------------------------
const CONTAINER_CLUSTER = [
    ...family("docker", "^docker\\s+(ps|images|logs|inspect|stats|top|port|diff|history|version|info|system\\s+info|system\\s+df|network\\s+ls|network\\s+inspect|volume\\s+ls|volume\\s+inspect|container\\s+ls|image\\s+ls)\\b", "容器与集群", [
        ["ps", "ps", "查看正在运行的 Docker 容器列表（只读，不启停容器）"],
        ["images", "images", "查看本地 Docker 镜像列表"],
        ["logs", "logs", "查看某个容器的运行日志"],
        ["inspect", "inspect", "查看容器/镜像的详细配置信息"],
        ["stats", "stats", "查看容器实时资源占用（CPU/内存）"],
        ["top", "top", "查看容器内正在运行的进程"],
        ["port", "port", "查看容器的端口映射情况"],
        ["diff", "diff", "查看容器文件系统相对镜像的改动"],
        ["history", "history", "查看镜像的构建历史"],
        ["version", "version", "查看 Docker 的版本信息"],
        ["info", "info", "查看 Docker 运行环境的整体信息"],
        ["system-info", "system info", "查看 Docker 系统整体信息"],
        ["system-df", "system df", "查看 Docker 占用的磁盘空间"],
        ["network-ls", "network ls", "查看 Docker 网络列表"],
        ["network-inspect", "network inspect", "查看某个 Docker 网络的详情"],
        ["volume-ls", "volume ls", "查看 Docker 数据卷列表"],
        ["volume-inspect", "volume inspect", "查看某个数据卷的详情"],
        ["container-ls", "container ls", "查看容器列表"],
        ["image-ls", "image ls", "查看镜像列表"],
    ]),
    ...family("docker-compose", "^docker-compose\\s+(ps|logs|config|images|top|version)\\b", "容器与集群", [
        ["ps", "ps", "查看 docker-compose 编排下的容器状态"],
        ["logs", "logs", "查看 docker-compose 服务日志"],
        ["config", "config", "查看 docker-compose 解析后的配置"],
        ["images", "images", "查看 docker-compose 用到的镜像列表"],
        ["top", "top", "查看容器内运行的进程"],
        ["version", "version", "查看 docker-compose 版本信息"],
    ]),
    ...family("docker compose", "^docker\\s+compose\\s+(ps|logs|config|images|top|version)\\b", "容器与集群", [
        ["ps", "ps", "查看 docker compose 编排下的容器状态"],
        ["logs", "logs", "查看 docker compose 服务日志"],
        ["config", "config", "查看 docker compose 解析后的配置"],
        ["images", "images", "查看 docker compose 用到的镜像列表"],
        ["top", "top", "查看容器内运行的进程"],
        ["version", "version", "查看 docker compose 版本信息"],
    ], "docker-compose-v2"),
    ...family("kubectl", "^kubectl\\s+(get|describe|logs|top|explain|api-resources|api-versions|cluster-info|config\\s+view|config\\s+get-contexts|version)\\b", "容器与集群", [
        ["get", "get", "查看 Kubernetes 资源列表（Pod/Service 等）"],
        ["describe", "describe", "查看 Kubernetes 资源的详细信息"],
        ["logs", "logs", "查看 Kubernetes Pod 的运行日志"],
        ["top", "top", "查看 Kubernetes 资源占用情况"],
        ["explain", "explain", "查看 Kubernetes 资源字段的说明文档"],
        ["api-resources", "api-resources", "查看集群支持的资源类型"],
        ["api-versions", "api-versions", "查看集群支持的 API 版本"],
        ["cluster-info", "cluster-info", "查看集群基础信息"],
        ["config-view", "config view", "查看 kubectl 当前配置"],
        ["config-get-contexts", "config get-contexts", "查看 kubectl 可切换的集群上下文列表"],
        ["version", "version", "查看 kubectl/集群版本信息"],
    ]),
];
// ---- 数据：开发质量检查 -----------------------------------------------------
const DEV_QUALITY = [
    exactCmd("bun-run-typecheck", "bun run typecheck", "跑项目的类型检查（只读检查代码，不改动文件）", "开发质量检查", "^bun\\s+run\\s+typecheck\\b$"),
    exactCmd("bun-run-typecheck-all", "bun run typecheck:all", "跑整个 monorepo 的类型检查", "开发质量检查", "^bun\\s+run\\s+typecheck:all\\b$"),
    wildcardCmd("bunx-tsc-noemit", "bunx tsc *--noEmit*", "用 TypeScript 编译器只做类型检查、不生成产物文件", "开发质量检查", "^bunx\\s+tsc\\b.*--noEmit\\b"),
    wildcardCmd("tsc-noemit", "tsc *--noEmit*", "用 TypeScript 编译器只做类型检查、不生成产物文件", "开发质量检查", "^tsc\\b.*--noEmit\\b"),
    exactCmd("npm-run-typecheck", "npm run typecheck", "跑项目的类型检查（只读检查代码，不改动文件）", "开发质量检查", "^npm\\s+run\\s+typecheck\\b$"),
    exactCmd("yarn-typecheck", "yarn typecheck", "跑项目的类型检查（只读检查代码，不改动文件）", "开发质量检查", "^yarn\\s+typecheck\\b$"),
    exactCmd("pnpm-typecheck", "pnpm typecheck", "跑项目的类型检查（DOSIA 自身开发常用命令，只读检查代码）", "开发质量检查", "^pnpm\\s+typecheck\\b$"),
];
// ---- 数据：帮助文档（整组已剔除）-------------------------------------------
// 原 3 条全部剔除，见文件头「伪只读剔除」：
//   - `Bash(* --help*)` / `Bash(* -h)`：**前导通配** = 给任意命令开口子。规则以 `*` 开头，
//     `evilcmd --help` / `rm -rf / -h ...` 之类只要末尾带该 flag 就静默放行，等于把地板对
//     「任意命令根」整体拆掉 —— 这是最严重的一类洞，直接删两条。
//   - `man`：分页器（MANPAGER / `!cmd` 逃逸），同 less/more，见 TEXT_PROCESSING 说明。
// 结果：不再有任何前导通配规则（防御测试断言 no rule starts with `Bash(*`）。
const ALL_SEEDS = [
    ...FILE_BROWSING,
    ...TEXT_PROCESSING,
    ...DATA_FORMAT,
    ...SYSTEM_INFO,
    ...NETWORK_DIAG,
    ...VERSION_CONTROL,
    ...PACKAGE_MGMT,
    ...VERSION_QUERIES,
    ...CONTAINER_CLUSTER,
    ...DEV_QUALITY,
];
/**
 * 已验证只读命令白名单 —— 唯一 typed 数据源（红线 #9）。转换/剔除策略见文件头。
 * 消费点：`buildCommandWhitelistAllowRules`（本文件）->
 * `session-manager.applyCommandWhitelist`（packages/core，会话构建时 union 进
 * SDK `settings.permissions.allow`）。
 */
export const COMMAND_WHITELIST = ALL_SEEDS;
/**
 * 唯一构建器（红线 #9）—— 按 disabledIds（员工在设置面板逐条关闭的白名单项）过滤后，
 * 返回可直接 union 进 SDK permissions.allow 的规则串数组。
 */
export function buildCommandWhitelistAllowRules(disabledIds = [], entries = COMMAND_WHITELIST) {
    if (disabledIds.length === 0)
        return entries.map((e) => e.rule);
    const disabled = new Set(disabledIds);
    return entries.filter((e) => !disabled.has(e.id)).map((e) => e.rule);
}
// ---- 数据：被拦命令的教学提示（craft blockedCommandHints 全量中文化） -------
export const BLOCKED_COMMAND_HINTS = [
    {
        command: "printf",
        reasonZh: "printf 不在默认只读白名单里，所以 Dosia 会先问你一下。",
        contextZh: "为了不让“看起来无害”的命令悄悄扩大权限，只读命令的清单刻意保持精简。",
        tryInsteadZh: [
            "简单打印文字/分隔符可以改用 echo。",
            "如果这条命令确实安全、以后不想再被问，可以在设置的「命令白名单」里手动加白。",
        ],
        exampleZh: "echo '--- 文件列表 ---'",
    },
    {
        command: "sed",
        reasonZh: "sed 会先问你一下——它即使加了 -n，也能用 w/W 命令把内容写进任意文件、用 e 命令执行系统命令，所以不放进「看一眼就自动放行」的清单。",
        contextZh: "sed -n 只是关掉「自动打印」，并不能保证这条命令只读；为安全起见 sed 的所有用法都先确认一次。",
        tryInsteadZh: [
            "只是想看/筛选文件内容，正常同意这次询问即可，不影响操作，只是多一步确认。",
            "纯查看可以改用 grep（搜关键词）或 cat/head/tail（看内容），这些无需确认。",
        ],
        exampleZh: "grep '关键词' 文件.txt   # 或 sed -n '1,80p' 文件.txt（会确认一次）",
    },
    {
        command: "find",
        reasonZh: "find 会先问你一下——它的 -exec/-delete 等参数能执行任意命令、删除文件，所以不放进「看一眼就自动放行」的清单。",
        contextZh: "find 表面是「查找文件」，但 -exec/-execdir 能对每个结果跑任意命令、-delete 能直接删文件，风险和普通只读查找完全不同，无法一概静默放行。",
        tryInsteadZh: [
            "只做查找、不带 -exec/-delete，正常同意这次询问即可。",
            "只按文件名找，也可以用编辑器/文件管理器的搜索；确需 find 批处理时同意询问即可。",
        ],
        exampleZh: "find . -name '*.md'   # 纯查找，会确认一次",
    },
    {
        command: "awk",
        reasonZh: "awk 脚本里如果调用了 system(...) 之类的语句，等于在借 awk 的壳执行别的命令，所以 Dosia 会先问你。",
        contextZh: "awk 本身是文本处理工具，但它支持在脚本里直接调用系统命令——这类用法和普通只读文本处理的风险完全不同，无法一概而论地静默放行。",
        tryInsteadZh: [
            "只做文本提取/统计，正常同意这次询问即可，之后同类简单用法不会每次都重复问细节。",
            "如果是自动化脚本要频繁用到复杂 awk，建议改造成不依赖 system() 调用的写法。",
        ],
    },
    {
        command: "gawk",
        reasonZh: "gawk 脚本里如果调用了 system(...) 之类的语句，等于借壳执行别的命令，所以会先询问你。",
        contextZh: "同 awk——文本处理工具本身安全，但它支持调用系统命令的写法需要单独确认。",
        tryInsteadZh: ["只做文本提取/统计，正常同意这次询问即可。"],
    },
    {
        command: "mawk",
        reasonZh: "mawk 脚本里如果调用了 system(...) 之类的语句，等于借壳执行别的命令，所以会先询问你。",
        contextZh: "同 awk——文本处理工具本身安全，但它支持调用系统命令的写法需要单独确认。",
        tryInsteadZh: ["只做文本提取/统计，正常同意这次询问即可。"],
    },
    {
        command: "nawk",
        reasonZh: "nawk 脚本里如果调用了 system(...) 之类的语句，等于借壳执行别的命令，所以会先询问你。",
        contextZh: "同 awk——文本处理工具本身安全，但它支持调用系统命令的写法需要单独确认。",
        tryInsteadZh: ["只做文本提取/统计，正常同意这次询问即可。"],
    },
    {
        command: "xargs",
        reasonZh: "xargs 会把读到的内容再拼成一条新命令去执行，等于给了 AI 一次“临时改变主意去跑别的命令”的机会，所以不在只读白名单里。",
        contextZh: "只读白名单只覆盖行为完全确定的命令；xargs 的最终行为取决于输入内容，无法提前判定是否安全。",
        tryInsteadZh: [
            "能用具体的只读命令直接完成的，就不用 xargs 中转。",
            "确实需要用 xargs 批量处理时，正常同意这次询问即可。",
        ],
    },
];
/**
 * 从一条 Bash 命令里提取“基础命令名”—— 跳过前置的环境变量赋值（如 FOO=bar cmd），
 * 取第一个真正的命令 token，并去掉路径前缀（/usr/bin/awk -> awk）。
 * 供 findBlockedCommandHint 内部使用，同时导出以便 UI/测试复用同一份提取逻辑
 * （红线 #9：单一实现，不在 UI 侧再手搓一份 parsing）。
 */
export function extractBaseCommand(command) {
    const tokens = command.trim().split(/\s+/);
    let i = 0;
    // 跳过形如 FOO=bar 的前置环境变量赋值 token。
    while (i < tokens.length && /^[A-Za-z_][A-Za-z0-9_]*=/.test(tokens[i])) {
        i++;
    }
    const raw = tokens[i] ?? "";
    const base = raw.split("/").pop() ?? raw;
    return base;
}
/**
 * 按命令基名查表返回教学提示（PermissionDialog 消费）。命中即代表这类命令 Dosia 有
 * 现成的人话解释；未命中返回 undefined（弹窗退回默认展示，不强行编造解释）。
 */
export function findBlockedCommandHint(command) {
    const base = extractBaseCommand(command);
    if (!base)
        return undefined;
    return BLOCKED_COMMAND_HINTS.find((h) => h.command === base);
}
//# sourceMappingURL=command-teaching-corpus.js.map